'use client';

import { AnalyticsSkuViewed } from '@ria-client/segment/Analytics/AnalyticsSkuViewed';
import { SEGMENT_SKU_VIEWED_SOURCE } from '@ria-client/segment/Analytics/constants';
import { MonetizableEnum } from '@ria-client/segment/enums';
import type { MiniProductCardEntityType as MarketplaceEntity } from '@ria-client/supergraph/structuredContent/hydration/MiniProductCard/types';
import { CtaButton, RatingSummary } from '@ria-client/tw-shared-ui';
import type { NodeProps } from '../types';
import { CtaImage } from './lib/cta-image';

export interface MiniProductCardProps extends Record<string, unknown> {
  id: string;
  marketplaceEntity?: MarketplaceEntity;
  align?: 'left' | 'right' | 'center' | 'none';
  useLargeWidth?: boolean;
  omitCta?: boolean;
  omitStarRating?: boolean;
}

export function MiniProductCard({ props }: NodeProps<MiniProductCardProps>) {
  const {
    align = 'none',
    marketplaceEntity,
    useLargeWidth = false,
    omitCta = false,
    omitStarRating = false,
  } = props;

  if (!marketplaceEntity) return null;

  const { institution, product } = marketplaceEntity;
  const analyticsPayload = {
    index: 0,
    ...(institution?.id && { institutionId: institution.id }),
    offerId: marketplaceEntity.id,
    ...(product?.id && { productId: product.id }),
    productType: marketplaceEntity.productType,
    monetizable: marketplaceEntity.monetizable as MonetizableEnum,
  };
  const cta =
    marketplaceEntity.cta?.subtext && marketplaceEntity.federallyInsuredAnnotation
      ? {
          ...marketplaceEntity.cta,
          subtext: `${marketplaceEntity.cta.subtext}, ${marketplaceEntity.federallyInsuredAnnotation}`,
        }
      : marketplaceEntity.cta;
  const alignment = {
    left: 'mx-auto md:float-left md:mr-8 md:ml-0',
    right: 'mx-auto md:float-right md:ml-8 md:mr-0',
    center: 'mx-auto',
    none: 'mx-auto',
  }[align];

  return (
    <AnalyticsSkuViewed
      impressionPosition={0}
      impressionSection="editorial_section"
      institutionId={institution?.id}
      institutionName={institution?.name}
      level="offer"
      monetizable={marketplaceEntity.monetizable as MonetizableEnum}
      name={marketplaceEntity.name}
      offerId={marketplaceEntity.id}
      offerName={marketplaceEntity.name}
      productId={product?.id}
      productName={product?.name}
      productType={marketplaceEntity.productType}
      source={SEGMENT_SKU_VIEWED_SOURCE}
    >
      <div>
        <div
          className={`mb-6 flex w-full flex-col gap-2 ${useLargeWidth ? 'max-w-[225px] md:w-[225px]' : 'max-w-[160px] md:w-[160px]'} ${alignment}`}
          data-testid="mini_product_card"
        >
          <CtaImage
            analyticsPayload={analyticsPayload}
            cta={omitCta ? undefined : marketplaceEntity.cta}
            displayName={marketplaceEntity.name}
            image={marketplaceEntity.image}
          />
          {!omitStarRating && (
            // Legacy StackedLayout: hideNumber, center-aligned, 13px link text.
            <div className="[&_a]:text-[13px]">
              <RatingSummary
                align="center"
                hideNumber
                layout="stacked"
                reviewLink={marketplaceEntity.reviewLink}
                starRating={marketplaceEntity.starRating}
                starRatingTooltip={marketplaceEntity.starRatingTooltip}
              />
            </div>
          )}
          {!omitCta && <CtaButton analyticsPayload={analyticsPayload} cta={cta} />}
        </div>
      </div>
    </AnalyticsSkuViewed>
  );
}
